import { useEditor, EditorContent, Extension, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { addRowAfter, addColumnAfter, TableMap } from '@tiptap/pm/tables';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { type RichDoc, EMPTY_RICH_DOC } from '../rich-text';
import Icon, { type IconName } from './Icon';

export { type RichDoc, EMPTY_RICH_DOC, richHasText } from '../rich-text';

/**
 * THE RICH-TEXT EDITOR — the charter is a real document, not a plain-text box.
 *
 * A PM or BA writes a scope statement in bullet lists, a risk register in a
 * table, headings between sections. Plain textareas cannot hold any of that, and
 * a charter is precisely the kind of document a company keeps, signs and reads
 * again later — so its content is stored as a STRUCTURED ProseMirror document
 * (JSONContent), not an opaque string. Structured content is what lets it be
 * rendered faithfully to the signed PDF and analysed again in the future
 * (headings, list items and table cells are addressable, not a wall of text).
 *
 * TipTap (over ProseMirror) is the editor; this wraps it in the one component the
 * app uses everywhere it needs formatted prose, with a toolbar sized and shaped
 * like the rest of the app's controls. The same component renders read-only
 * (editable=false, no toolbar), so what a reader sees is what the author wrote.
 */

const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    link: { openOnClick: false, HTMLAttributes: { rel: 'noopener nofollow', target: '_blank' } },
  }),
  TableKit.configure({ table: { resizable: false } }),
];

/**
 * PLACEHOLDER — a hint in an empty field, so a blank box says what belongs in it.
 *
 * The @tiptap/extension-placeholder package is not installed and this needs no
 * dependency: a one-plugin extension marks the single empty paragraph with the
 * `is-empty` class and the hint text, which CSS renders as grey prompt text. Only
 * while editable and only when the document is truly empty.
 */
const Placeholder = Extension.create<{ placeholder: string }>({
  name: 'rtePlaceholder',
  addOptions() {
    return { placeholder: '' };
  },
  addProseMirrorPlugins() {
    const text = this.options.placeholder;
    const editor = this.editor;
    return [
      new Plugin({
        props: {
          decorations: (state) => {
            if (!text || !editor.isEditable) return null;
            const { doc } = state;
            const empty =
              doc.childCount === 1 && doc.firstChild != null && doc.firstChild.isTextblock && doc.firstChild.content.size === 0;
            if (!empty) return null;
            const node = doc.firstChild!;
            return DecorationSet.create(doc, [
              Decoration.node(0, node.nodeSize, { class: 'is-empty', 'data-placeholder': text }),
            ]);
          },
        },
      }),
    ];
  },
});

/** Add a row to the end of the table at `tablePos`, wherever the caret is. The
 *  button under each table (below) calls this, so a row can be added without
 *  first knowing to click inside the table — the discoverability fix for the
 *  seeded Milestones / Risk / RACI tables. */
function addRowToTable(editor: Editor, tablePos: number) {
  const table = editor.state.doc.nodeAt(tablePos);
  if (!table || table.type.name !== 'table') return;
  const map = TableMap.get(table);
  const start = tablePos + 1; // the table's content start
  const lastRowFirstCell = start + map.map[(map.height - 1) * map.width];
  const sel = TextSelection.near(editor.state.doc.resolve(lastRowFirstCell + 1));
  const tr = editor.state.tr.setSelection(sel);
  editor.view.dispatch(tr);
  addRowAfter(editor.view.state, editor.view.dispatch);
  editor.view.focus();
}

/** Add a column at the end of the table at `tablePos`. */
function addColumnToTable(editor: Editor, tablePos: number) {
  const table = editor.state.doc.nodeAt(tablePos);
  if (!table || table.type.name !== 'table') return;
  const map = TableMap.get(table);
  const start = tablePos + 1;
  const topRowLastCell = start + map.map[map.width - 1];
  const sel = TextSelection.near(editor.state.doc.resolve(topRowLastCell + 1));
  const tr = editor.state.tr.setSelection(sel);
  editor.view.dispatch(tr);
  addColumnAfter(editor.view.state, editor.view.dispatch);
  editor.view.focus();
}

/** The persistent "+ Add row / + Add column" strip rendered under a table. */
function tableControlsDom(editor: Editor, tablePos: number): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'rte-table-add';
  wrap.contentEditable = 'false';
  const make = (label: string, run: () => void) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'rte-table-add-btn';
    b.textContent = label;
    // Keep the document selection; run on click.
    b.addEventListener('mousedown', (e) => e.preventDefault());
    b.addEventListener('click', (e) => {
      e.preventDefault();
      run();
    });
    return b;
  };
  wrap.appendChild(make('+ Add row', () => addRowToTable(editor, tablePos)));
  wrap.appendChild(make('+ Add column', () => addColumnToTable(editor, tablePos)));
  return wrap;
}

/**
 * TABLE CONTROLS — a persistent "+ Add row / + Add column" under every table.
 *
 * The toolbar's table buttons only appear while the caret is inside a table, so a
 * first-timer never finds how to extend the seeded charter tables ("there is no
 * way to do that now"). A widget decoration after each table gives an always-
 * visible, unambiguous control attached to THAT table. Editable only.
 */
const TableControls = Extension.create({
  name: 'rteTableControls',
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        props: {
          decorations: (state) => {
            if (!editor.isEditable) return null;
            const decos: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (node.type.name !== 'table') return undefined;
              decos.push(
                Decoration.widget(pos + node.nodeSize, () => tableControlsDom(editor, pos), {
                  side: 1,
                  ignoreSelection: true,
                  key: `rte-table-add-${pos}`,
                })
              );
              return false; // no tables nest inside tables
            });
            return decos.length ? DecorationSet.create(state.doc, decos) : null;
          },
        },
      }),
    ];
  },
});

// A letter-mark button (Bold, Italic, Strike, H2, H3). These stay as glyphs, not
// pictures — a bold "B" and a slanted "I" ARE the icons for them, and a heading
// reads clearer as its level than as any shape.
const glyph = (name: string, mark: string): ReactNode => (
  <span className={`rte-glyph rte-glyph--${mark}`}>{name}</span>
);

function Toolbar({ editor }: { editor: Editor }) {
  // Re-render on every selection/content change so the active states are honest.
  const btn = (
    content: ReactNode,
    aria: string,
    isActive: boolean,
    run: () => void,
    disabled = false,
    extra = ''
  ) => (
    <button
      type="button"
      className={`rte-tool ${isActive ? 'is-active' : ''} ${extra}`.trim()}
      aria-label={aria}
      aria-pressed={isActive}
      title={aria}
      disabled={disabled}
      // Keep focus in the document so the command applies to the selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
    >
      {content}
    </button>
  );

  // An icon button — the same rte-tool, its label carried by title/aria only.
  const ibtn = (
    icon: IconName,
    aria: string,
    isActive: boolean,
    run: () => void,
    disabled = false,
    extra = ''
  ) => btn(<Icon name={icon} size={14} />, aria, isActive, run, disabled, `rte-tool--icon ${extra}`.trim());

  // LINK EDITING — a themed in-app field, never a native window.prompt. A native
  // dialog can't be themed (wrong in dark), can't match the app's keyboard flow,
  // and is exactly the class of surface the app's own standards forbid. The
  // selection lives in the editor STATE, so it survives DOM focus moving to the
  // input; applying runs against it via extendMarkRange('link').
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkWrap = useRef<HTMLDivElement>(null);

  const openLink = () => {
    setLinkUrl((editor.getAttributes('link').href as string) || 'https://');
    setLinkOpen(true);
  };
  const applyLink = () => {
    const url = linkUrl.trim();
    if (url === '' || url === 'https://') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkOpen(false);
  };
  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  // Close the link field on Escape and on a click outside it (the app standard
  // for every popover). The wrap contains both the opener and the field, so
  // clicking the opener toggles rather than double-firing.
  useEffect(() => {
    if (!linkOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLinkOpen(false);
        editor.chain().focus().run();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (linkWrap.current && !linkWrap.current.contains(e.target as Node)) setLinkOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [linkOpen, editor]);

  const inTable = editor.isActive('table');

  return (
    <div className="rte-toolbar" role="toolbar" aria-label="Formatting">
      <div className="rte-group">
        {btn(
          <span className="rte-glyph rte-glyph--h">H<sub>2</sub></span>,
          'Heading',
          editor.isActive('heading', { level: 2 }),
          () => editor.chain().focus().toggleHeading({ level: 2 }).run()
        )}
        {btn(
          <span className="rte-glyph rte-glyph--h">H<sub>3</sub></span>,
          'Subheading',
          editor.isActive('heading', { level: 3 }),
          () => editor.chain().focus().toggleHeading({ level: 3 }).run()
        )}
      </div>
      <div className="rte-group">
        {btn(glyph('B', 'b'), 'Bold', editor.isActive('bold'), () => editor.chain().focus().toggleBold().run())}
        {btn(glyph('I', 'i'), 'Italic', editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run())}
        {btn(glyph('S', 's'), 'Strikethrough', editor.isActive('strike'), () =>
          editor.chain().focus().toggleStrike().run()
        )}
      </div>
      <div className="rte-group">
        {ibtn('list-bullet', 'Bullet list', editor.isActive('bulletList'), () =>
          editor.chain().focus().toggleBulletList().run()
        )}
        {ibtn('list-ordered', 'Numbered list', editor.isActive('orderedList'), () =>
          editor.chain().focus().toggleOrderedList().run()
        )}
        {ibtn('quote', 'Quote', editor.isActive('blockquote'), () =>
          editor.chain().focus().toggleBlockquote().run()
        )}
      </div>
      <div className="rte-group">
        <div className="rte-link-wrap" ref={linkWrap}>
          {ibtn('link', 'Add or edit link', editor.isActive('link') || linkOpen, () =>
            linkOpen ? setLinkOpen(false) : openLink()
          )}
          {linkOpen && (
            <div className="rte-link-pop" role="group" aria-label="Link">
              <input
                className="rte-link-input input"
                type="url"
                value={linkUrl}
                autoFocus
                placeholder="https://…"
                aria-label="Link URL"
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                }}
              />
              <button type="button" className="btn btn-small" onMouseDown={(e) => e.preventDefault()} onClick={applyLink}>
                Apply
              </button>
              {editor.isActive('link') && (
                <button
                  type="button"
                  className="btn btn-small rte-link-remove"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={removeLink}
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
        {/* Insert table is a one-shot action, not a toggle — it must never show a
            pressed/active state (that misread it as "a table is on" and misled
            screen readers). `inTable` only gates the table-EDITING group below. */}
        {ibtn('table', 'Insert table', false, () =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        )}
      </div>
      {/* The table-editing controls appear only while the caret is inside a
          table — otherwise they sit greyed-out on every field for no reason. */}
      {inTable && (
        <div className="rte-group rte-group--table">
          {ibtn('table-col-add', 'Add column', false, () => editor.chain().focus().addColumnAfter().run())}
          {ibtn('table-col-del', 'Delete column', false, () => editor.chain().focus().deleteColumn().run(), false, 'rte-tool--danger')}
          {ibtn('table-row-add', 'Add row', false, () => editor.chain().focus().addRowAfter().run())}
          {ibtn('table-row-del', 'Delete row', false, () => editor.chain().focus().deleteRow().run(), false, 'rte-tool--danger')}
          {ibtn('table-remove', 'Delete table', false, () => editor.chain().focus().deleteTable().run(), false, 'rte-tool--danger')}
        </div>
      )}
    </div>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  editable = true,
  placeholder,
  ariaLabel,
  ariaRequired,
}: {
  value?: RichDoc | null;
  onChange?: (doc: RichDoc) => void;
  editable?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Mirror the visible required asterisk to assistive tech (R10). */
  ariaRequired?: boolean;
}) {
  // Per-instance so the placeholder text (a prop) reaches its plugin; stable
  // across renders unless the placeholder changes.
  const extensions = useMemo(
    () => [...EXTENSIONS, TableControls, Placeholder.configure({ placeholder: placeholder ?? '' })],
    [placeholder]
  );
  const editor = useEditor({
    extensions,
    content: value ?? EMPTY_RICH_DOC,
    editable,
    editorProps: {
      attributes: {
        class: 'rte-content',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...(ariaRequired ? { 'aria-required': 'true' } : {}),
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getJSON()),
  });

  // Keep the editor's editability in step with the prop (a viewer who is granted
  // edit rights mid-session should not have to reload to get a working toolbar).
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  // GUARD: never let an editable editor sit inside a <label>. A <label> forwards
  // any click in it to its first labelable descendant — here the H₂ toolbar
  // button — so clicking in to type silently turned the first line into a
  // heading. The toolbar of buttons is not a single labelable control; its
  // caption must be a plain <span> in a div.stack, not a wrapping <label>. This
  // warns in dev if a caller ever reintroduces the wrapper. (Banked lesson.)
  useEffect(() => {
    if (!editor || !editable || !import.meta.env?.DEV) return;
    if (editor.view.dom.closest('label')) {
      console.warn(
        'RichTextEditor is inside a <label>: clicks will be forwarded to the first toolbar button and headingify the field. Use a div.stack with a <span> caption instead.'
      );
    }
  }, [editor, editable]);

  // A READ-ONLY render tracks its value — if what it shows changes (a live save
  // from elsewhere, switching to another project) the text updates. An EDITABLE
  // editor owns its content after mount, so it is NOT re-set here: doing so on
  // every keystroke (onChange lifts the value up, which would flow back in) would
  // fight the caret. The edit form mounts fresh with the current value, which is
  // where an editable editor gets its starting content.
  useEffect(() => {
    if (!editor || editable) return;
    const next = value ?? EMPTY_RICH_DOC;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(next)) {
      editor.commands.setContent(next);
    }
  }, [editor, editable, value]);

  if (!editor) return null;

  if (!editable) {
    // Read-only: the same rendering, no toolbar, no editing chrome.
    return (
      <div className="rte rte-readonly">
        <EditorContent editor={editor} />
      </div>
    );
  }

  return (
    <div className="rte">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
